import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import ContentEditor from "./ContentEditor";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Save, UploadCloud, RotateCcw, ExternalLink, Eye } from "lucide-react";

const LABELS = {
  site: "Site (Header, Footer, Global)",
  home: "Home",
  steakhouse: "Steakhouse",
  tavern: "Tavern",
  venue: "Venue",
  gallery: "Gallery",
  contact: "Contact",
  careers: "Careers",
};

const PageEditor = () => {
  const { slug } = useParams();
  const [draft, setDraft] = useState(null);
  const [published, setPublished] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/api/cms/pages/${slug}`);
        if (!alive) return;
        setDraft(data.draft || {});
        setPublished(data.published || {});
      } catch {
        if (alive) toast.error("Nuk u ngarkua faqja");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const dirty = useMemo(() => {
    try {
      return JSON.stringify(draft || {}) !== JSON.stringify(published || {});
    } catch {
      return false;
    }
  }, [draft, published]);

  const saveDraft = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/api/cms/pages/${slug}`, { draft });
      setDraft(data.draft || {});
      toast.success("Drafti u ruajt");
    } catch {
      toast.error("Ruajtja dështoi");
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      // Save current draft first so publish uses latest edits
      await api.put(`/api/cms/pages/${slug}`, { draft });
      const { data } = await api.post(`/api/cms/pages/${slug}/publish`);
      setDraft(data.draft || {});
      setPublished(data.published || {});
      toast.success("Faqja u publikua");
    } catch {
      toast.error("Publikimi dështoi");
    } finally {
      setPublishing(false);
    }
  };

  const revert = async () => {
    if (!window.confirm("Të hidhen ndryshimet e paruajtura dhe të kthehet te versioni i publikuar?")) return;
    try {
      const { data } = await api.post(`/api/cms/pages/${slug}/revert`);
      setDraft(data.draft || {});
      setPublished(data.published || {});
      toast.success("U kthye te versioni i publikuar");
    } catch {
      toast.error("Kthimi dështoi");
    }
  };

  const publicHref = slug === "site" ? "/" : `/${slug}`;

  const previewDraft = async () => {
    try {
      await api.put(`/api/cms/pages/${slug}`, { draft });
      window.open(`${publicHref}?preview=${slug}`, "_blank");
    } catch {
      toast.error("Nuk u hap parapamja");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div data-testid="page-editor">
      <Link to="/admin/pages" className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900" data-testid="editor-back">
        <ArrowLeft size={14} /> Të gjitha faqet
      </Link>

      <div className="mt-4 flex items-start justify-between gap-4 sticky top-0 z-10 bg-zinc-50/90 backdrop-blur py-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-medium text-zinc-900">{LABELS[slug] || slug}</h1>
            {dirty ? (
              <span data-testid="editor-status-dirty" className="text-[10px] tracking-wider uppercase text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">Ndryshime pa publikuar</span>
            ) : (
              <span data-testid="editor-status-clean" className="text-[10px] tracking-wider uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">E publikuar</span>
            )}
          </div>
          <a href={publicHref} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mt-1" data-testid="editor-view-public">
            Shiko në sajt <ExternalLink size={12} />
          </a>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {dirty && (
            <button onClick={revert} className="inline-flex items-center gap-1.5 text-xs text-zinc-600 hover:text-zinc-900 border border-zinc-300 rounded-md px-3 py-2" data-testid="editor-revert-btn">
              <RotateCcw size={14} /> Kthe
            </button>
          )}
          <button onClick={previewDraft} className="inline-flex items-center gap-1.5 text-xs text-zinc-700 hover:text-zinc-900 border border-zinc-300 rounded-md px-3 py-2" data-testid="editor-preview-btn">
            <Eye size={14} /> Preview Draft
          </button>
          <button onClick={saveDraft} disabled={saving} className="inline-flex items-center gap-1.5 text-xs text-zinc-700 hover:text-zinc-900 border border-zinc-300 rounded-md px-3 py-2 disabled:opacity-60" data-testid="editor-save-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Ruaj draftin
          </button>
          <button onClick={publish} disabled={publishing} className="inline-flex items-center gap-1.5 text-xs bg-zinc-900 text-white rounded-md px-4 py-2 hover:bg-zinc-800 disabled:opacity-60" data-testid="editor-publish-btn">
            {publishing ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Publiko
          </button>
        </div>
      </div>

      <div className="mt-4">
        <ContentEditor value={draft} onChange={setDraft} path={slug} />
      </div>
    </div>
  );
};

export default PageEditor;
